import { sendRequest } from "@infrastructure/api/api";
import ApiResponse from "@core/types/apiResponse";
import { ApiMessageResponse } from "@core/types/apiMessageResponse";
import { ReportTemplateResponse } from "@features/report/template/types/reportTemplate";
import { ReportTemplatePreviewResponse } from "@features/report/template/types/reportTemplatePreview";

/**
 * Fetches a report template for a given department using provided query parameters.
 *
 * Sends a GET request to `department/{departmentId}/reporttemplate/query` with `params`
 * serialized into the query string via `URLSearchParams`.
 *
 * @param departmentId - The numeric ID of the department to query.
 * @param params - An object of query parameters to include in the request. Keys and values should be
 *                 serializable to strings (they will be passed to `URLSearchParams`). For arrays or
 *                 complex values, stringify them before passing.
 * @returns A promise that resolves to an `ApiResponse<ReportTemplateResponse>` containing the API response.
 * @throws Rethrows errors from `sendRequest` (for example network errors or non-OK responses depending on its implementation).
 *
 */
/** Convert booleans to 1/0 and strip null/undefined before URLSearchParams */
const toQueryParams = (params: Record<string, any>): Record<string, string> => {
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null) continue;
        result[key] = typeof value === 'boolean' ? (value ? '1' : '0') : String(value);
    }
    return result;
};

export const fetchTemplate = async (departmentId: number, params: any): Promise<ApiResponse<ReportTemplateResponse>> => {
    const response = await sendRequest<any, ReportTemplateResponse>('GET', `department/${departmentId}/reporttemplate/query?${new URLSearchParams(toQueryParams(params))}`);
    return response;
};


/**
 * Fetches assigned report templates for a church.
 * @param churchId - The ID of the church to fetch templates for.
 * @param departmentId - The ID of the department to fetch templates for.
 * @param params - The parameters to fetch templates for.
 * @returns A promise that resolves to an ApiResponse<ReportTemplateResponse>.
 */
export const fetchAssignedTemplateForChruchAsync = async (churchId: number, departmentId: number, params: any): Promise<ApiResponse<ReportTemplateResponse>> => {
    const response = await sendRequest<any, ReportTemplateResponse>('GET', `/church/${churchId}/department/${departmentId}/reporttemplate/query?${new URLSearchParams(toQueryParams(params))}`);
    return response;
};

export const fetchAssignedTemplateForDistrictAsync = async (
    districtId: number,
    departmentId: number,
    params: any
): Promise<ApiResponse<ReportTemplateResponse>> => {
    const query = new URLSearchParams({
        ...toQueryParams(params),
        department_id: String(departmentId),
    });
    return sendRequest<any, ReportTemplateResponse>(
        'GET',
        `/district/${districtId}/reporttemplate/query?${query.toString()}`
    );
};

export const fetchAssignedTemplateForNationAsync = async (
    nationId: number,
    departmentId: number,
    params: any
): Promise<ApiResponse<ReportTemplateResponse>> => {
    const query = new URLSearchParams({
        ...toQueryParams(params),
        department_id: String(departmentId),
    });
    return sendRequest<any, ReportTemplateResponse>(
        'GET',
        `/nation/${nationId}/reporttemplate/query?${query.toString()}`
    );
};

/**
 * Fetches report template preview. When scope (nationId, districtId, or churchId) is provided, uses scoped endpoint.
 * @param departmentId - Department ID
 * @param reportTemplateId - Report template ID
 * @param scope - Optional. When provided, uses e.g. nation/{nationId}/department/.../preview
 */
export const fetchTemplatePreview = async (
    departmentId: number,
    reportTemplateId: number,
    scope?: { nationId?: number; districtId?: number; churchId?: number }
): Promise<ApiResponse<ReportTemplatePreviewResponse>> => {
    let path: string;
    if (scope?.nationId) {
        path = `nation/${scope.nationId}/department/${departmentId}/reporttemplate/${reportTemplateId}/preview`;
    } else if (scope?.districtId) {
        path = `district/${scope.districtId}/department/${departmentId}/reporttemplate/${reportTemplateId}/preview`;
    } else if (scope?.churchId) {
        path = `church/${scope.churchId}/department/${departmentId}/reporttemplate/${reportTemplateId}/preview`;
    } else {
        path = `department/${departmentId}/reporttemplate/${reportTemplateId}/preview`;
    }
    const response = await sendRequest<any, ReportTemplatePreviewResponse>('GET', path);
    return response;
};

/**
 * Create a new report template for a specific nation and department.
 *
 * Sends a POST request to the backend endpoint for creating report templates and returns the parsed API response.
 *
 * @param nationId - The numeric identifier of the nation to which the template belongs.
 * @param departmentId - The numeric identifier of the department to which the template belongs.
 * @param data - The request payload containing template details (shape determined by server schema).
 * @returns A promise that resolves to an ApiResponse wrapping the created ReportTemplateResponse.
 * @throws Will throw if the HTTP request fails or the response cannot be parsed as the expected type.
 */
export const createTemplate = async (nationId: number, departmentId: number, data: any): Promise<ApiResponse<ApiMessageResponse>> => {
    const response = await sendRequest<any, ApiMessageResponse>('POST', `nation/${nationId}/department/${departmentId}/reporttemplate/create`, data);
    return response;
};

/**
 * Update a report template for a specific nation and department.
 *
 * Sends a POST request to the endpoint:
 * `nation/{nationId}/department/{departmentId}/reporttemplate/{templateId}/update`
 * with the provided `data` payload and returns the API response containing the updated template.
 *
 * @param nationId - Numeric identifier of the nation that owns the department.
 * @param departmentId - Numeric identifier of the department that owns the template.
 * @param templateId - Numeric identifier of the report template to update.
 * @param data - Payload containing the fields to update on the report template.
 *
 * @returns A Promise that resolves to an ApiResponse<ReportTemplate> with the updated template.
 *
 * @throws Will reject if the underlying HTTP request fails or the API responds with an error.
 *
 */
export const updateTemplate = async (nationId: number, departmentId: number, templateId: number, data: any): Promise<ApiResponse<ApiMessageResponse>> => {
    const response = await sendRequest<any, ApiMessageResponse>('POST', `nation/${nationId}/department/${departmentId}/reporttemplate/${templateId}/update`, data);
    return response;
};

/**
 * Assign activities to a report template for a specific department within a nation.
 *
 * Sends a POST request to the API endpoint that attaches one or more activities to the
 * specified report template and returns the server's response containing the updated template.
 *
 * @param nationId - The numeric ID of the nation that owns the department/template.
 * @param departmentId - The numeric ID of the department containing the report template.
 * @param templateId - The numeric ID of the report template to which activities will be assigned.
 * @param data - Request payload describing the activity assignment details (e.g., activity IDs or objects). The exact shape is determined by the backend API.
 *
 * @returns A promise that resolves to an ApiResponse wrapping a ReportTemplateResponse representing the updated template.
 *
 * @throws {Error} If the underlying HTTP request fails or the API responds with an error status.
 *
 */
export const assignTemplateActivities = async (nationId: number, departmentId: number, templateId: number, data: any): Promise<ApiResponse<ApiMessageResponse>> => {
    const response = await sendRequest<any, ApiMessageResponse>('POST', `nation/${nationId}/department/${departmentId}/reporttemplate/${templateId}/activity/assign`, data);
    return response;
};

export const removeTemplateActivities = async (
    nationId: number,
    departmentId: number,
    templateId: number,
    data: { activity_ids: number[] }
): Promise<ApiResponse<ApiMessageResponse>> => {
    const response = await sendRequest<{ activity_ids: number[] }, ApiMessageResponse>(
        'POST',
        `nation/${nationId}/department/${departmentId}/reporttemplate/${templateId}/activity/remove`,
        data
    );
    return response;
};

/** Disables a report template (sets it inactive). */
export const disableTemplate = async (nationId: number, departmentId: number, templateId: number): Promise<ApiResponse<ApiMessageResponse>> => {
    return sendRequest<never, ApiMessageResponse>('POST', `nation/${nationId}/department/${departmentId}/reporttemplate/${templateId}/disable`);
};

/** Enables a report template (sets it active). */
export const enableTemplate = async (nationId: number, departmentId: number, templateId: number): Promise<ApiResponse<ApiMessageResponse>> => {
    return sendRequest<never, ApiMessageResponse>('POST', `nation/${nationId}/department/${departmentId}/reporttemplate/${templateId}/enable`);
};

/** Changes the origin scope (who completes this template) for a report template. */
export const changeTemplateOriginScope = async (
    nationId: number,
    departmentId: number,
    templateId: number,
    data: { new_origin_scope: 'CHURCH' | 'DISTRICT' | 'NATION' }
): Promise<ApiResponse<ApiMessageResponse>> => {
    return sendRequest<{ new_origin_scope: string }, ApiMessageResponse>(
        'POST',
        `nation/${nationId}/department/${departmentId}/reporttemplate/${templateId}/changeorigin`,
        data
    );
};
